import React, { ReactNode, Suspense, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";

import { MessageTypeList } from "@intelligems/ig-types";
import { Box } from "@mui/material";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { QueryErrorResetBoundary } from "@tanstack/react-query";

import { useGetOrg } from "../api/organizations/get-organization";
import { useUser } from "../api/user-auths/login";
import { useGetUserHubspotToken } from "../api/users/get-user-hubspot-token";
import { ScopedDialog } from "../components/Dialogs/ScopedDialog";
import { SetDomainDialog } from "../components/Dialogs/SetDomainDialog";
import {
  checkAnyScope,
  checkBooleanScope,
} from "../components/ScopeWrappers/helpers";
import { usePrefetchQueries } from "../hooks/prefetchQueries";
import { useQueryParam } from "../hooks/util";
import {
  appendNewOrg,
  setViewedCampaignDialog,
  setViewedInfoPopup,
  setViewedMigrateCampaign,
} from "../store/slices/appSettings";
import { setDialog } from "../store/slices/global";
import { IAppState } from "../store/types/store";
import { Logout } from "../utils";
import Loading from "../utils/Loading";

import { DialogProvider } from "./DialogProvider";
import { WebsocketProvider } from "./WebsocketProvider";

interface LoggedInProviderProps {
  children?: ReactNode;
}
export const LoggedInProvider = ({ children }: LoggedInProviderProps) => {
  usePrefetchQueries();

  const dispatch = useDispatch();
  const organization = useGetOrg();
  const user = useUser();
  const viewedInfoPopup = useSelector(
    (state: IAppState) => state.appSettings.viewedCampaignPopup
  );
  const isAdmin = useIsAdmin();

  const widgetAuth = useQueryParam("widgetAuth");

  let logoutWorker: SharedWorker;

  useEffect(() => {
    try {
      if (!logoutWorker) {
        logoutWorker = new SharedWorker("./workers/logout-worker.js", {
          type: "module",
          name: "Global Logout",
        });
      }
    } catch (e) {
      console.log(e);
    }

    if (logoutWorker) {
      logoutWorker.port.onmessage = () => {
        Logout();
      };
    } else if (!widgetAuth) {
      Logout();
    }
  }, []);

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      const type = e.data?.type;
      if (!MessageTypeList.includes(type)) {
        return null;
      }

      if (type === "LOG_OUT") {
        logoutWorker.port.postMessage({ test: "test" });
      }
    },
    [user.isLoading, user.data]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (
      organization.data.org.shopId !== null &&
      organization.data.org.name !== null
    ) {
      dispatch(
        appendNewOrg({
          shopId: organization.data.org.shopId!,
          name: organization.data.org.name,
        })
      );
    }
  }, [dispatch, organization.data.org.shopId, organization.data.org.name]);

  useEffect(() => {
    const hasPersonalizationPermissions = checkAnyScope([
      isAdmin ?? false,
      checkBooleanScope(
        "personalizations",
        organization?.data.org.configurations
      ),
    ]);
    if (user.isSuccess && user.data.user.userAlerts) {
      if (
        hasPersonalizationPermissions &&
        user.data.user.userAlerts["showPersonalizationsDialog"] &&
        !viewedInfoPopup
      ) {
        dispatch(setDialog({ dialog: "showUserInfoPopup", open: true }));
      } else if (!user.data.user.userAlerts["showPersonalizationsDialog"]) {
        // set redux state to true since has already seen it before
        dispatch(setViewedCampaignDialog());
      }
      if (
        hasPersonalizationPermissions &&
        !user.data.user.userAlerts["showPersonalizationMigrateBanner"]
      ) {
        dispatch(setViewedMigrateCampaign());
      }
    }
  }, [
    organization.isSuccess,
    user.data?.user?.userAlerts,
    user.isSuccess,
    isAdmin,
  ]);

  useEffect(() => {
    if (user.isSuccess && user.data) {
      window?.Usersnap?.on("open", function (event) {
        event.api.setValue("visitor", user.data.user.email);
      });
    }
  }, [user.data, user.isSuccess]);

  return (
    <Box>
      <WebsocketProvider />
      <ScopedDialog />
      <SetDomainDialog />
      <QueryErrorResetBoundary>
        <Suspense fallback={<Loading />}>
          <DialogProvider />
          {children ? children : <Outlet />}
        </Suspense>
      </QueryErrorResetBoundary>
    </Box>
  );
};
